import { Directive, ElementRef, forwardRef, HostListener, Input, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CountryCode } from 'libphonenumber-js';
import { isNullOrUndefined, PhoneNumberUtil } from '../../utils';

/**
 * Generated class for the PhoneNumberInputDirective directive.
 *
 * See https://angular.io/docs/ts/latest/api/core/index/DirectiveMetadata-class.html
 * for more info on Angular Directives.
 */
@Directive({
  selector: '[uzPhoneNumberInput]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberInputDirective),
      multi: true
    }
  ],
  standalone: true
})
export class PhoneNumberInputDirective implements ControlValueAccessor {
  @Input() pniCountryCode: CountryCode = 'US';

  private _phoneNumberString = '';

  constructor(private _renderer: Renderer2, private _elementRef: ElementRef) {}

  @HostListener('blur', []) onBlur() {
    this.onTouchedCallback();
  }

  @HostListener('focus', []) onFocus() {
    this.onTouchedCallback();
  }

  @HostListener('paste', ['$event']) onPaste(e: ClipboardEvent) {
    this.updateControlValue(e.clipboardData.getData('text'));
    e.preventDefault();
  }

  @HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
    // Allow copy shortcut
    if ((e.ctrlKey || e.metaKey) && e.keyCode === 67) {
      return;
    }
    // Allow paste shortcut
    if ((e.ctrlKey || e.metaKey) && e.keyCode === 86) {
      return;
    }
    // Switch single keys
    switch (e.key) {
      case 'Tab':
      case 'ArrowLeft':
      case 'ArrowRight':
        // Allow default
        break;
      case 'Backspace':
        this.handleBackspaceKeyPress(e);
        break;
      default:
        if (isNaN(+e.key)) {
          e.preventDefault();
        } else {
          this.handleNumericKeyPress(e);
        }
        break;
    }
  }

  onChangeCallback = (value: any) => {};
  onTouchedCallback = () => {};

  writeValue(value: any) {
    this._phoneNumberString = isNullOrUndefined(value)
      ? ''
      : value
          .toString()
          .replace('+1', '')
          .replace(/[^0-9]/g, '');

    this._renderer.setProperty(this._elementRef.nativeElement, 'value', this.buildElementValue());
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this._renderer.setProperty(this._elementRef.nativeElement, 'disabled', isDisabled);
  }

  private updateControlValue(value: any, cursorPosition?: number) {
    this._phoneNumberString = isNullOrUndefined(value)
      ? ''
      : value
          .toString()
          .replace('+1', '')
          .replace(/[^0-9]/g, '');

    this._renderer.setProperty(this._elementRef.nativeElement, 'value', this.buildElementValue());
    if (cursorPosition) {
      setTimeout(() => {
        this._elementRef.nativeElement.selectionStart = cursorPosition;
        this._elementRef.nativeElement.selectionEnd = cursorPosition;
      });
    }
    this.onChangeCallback(this.buildControlValue());
  }

  private handleBackspaceKeyPress(e: any) {
    e.preventDefault();
    // Calculate where the cursor is in relation to the underlying unformatted phone number
    const offsetAmount = this.getFormattedCursorOffset(e);

    if (this._phoneNumberString.length === 0) {
      this.updateControlValue(this._phoneNumberString);
      return;
    }

    if (this._phoneNumberString.length === 1) {
      this.updateControlValue('');
      return;
    }

    if (e.target.selectionStart === e.target.selectionEnd) {
      this.updateControlValue(
        this._phoneNumberString.slice(0, e.target.selectionStart - offsetAmount - 1) +
          this._phoneNumberString.slice(e.target.selectionStart - offsetAmount, this._phoneNumberString.length),
        e.target.selectionStart -
          /* have to subtract an extra space if we're reversing into the area code */ (e.target.selectionStart === 5 ? 2 : 1)
      );
      return;
    }

    this.updateControlValue(
      this._phoneNumberString.slice(0, e.target.selectionStart - offsetAmount) +
        this._phoneNumberString.slice(e.target.selectionEnd - offsetAmount, this._phoneNumberString.length),
      e.target.selectionStart
    );
  }

  private getFormattedCursorOffset(e: any) {
    let offsetAmount = 0;
    const formatted = PhoneNumberUtil.asYouType(this._phoneNumberString, this.pniCountryCode);

    switch (e.target.selectionStart) {
      case 1:
      case 2:
      case 3:
      case 4:
        // excludes first paren
        offsetAmount = 1;
        break;
      case 5:
        //excludes second paren
        offsetAmount = 2;
        break;
      case 6:
      case 7:
      case 8:
      case 9:
        // excludes space after area code
        offsetAmount = 3;
        break;
      case 10:
      case 11:
      case 12:
      case 13:
      case 14:
        // excludes dash
        offsetAmount = 4;
        break;
    }

    if (formatted.startsWith('1 ')) {
      offsetAmount = offsetAmount + 2;
    }

    return offsetAmount;
  }

  private handleNumericKeyPress(e: any) {
    e.preventDefault();

    let offsetAmount = this.getFormattedCursorOffset(e);

    // Don't add more characters if the phone number is already valid and nothing is selected
    if (
      PhoneNumberUtil.isValidPhoneNumber(this._phoneNumberString, this.pniCountryCode) &&
      e.target.selectionStart === e.target.selectionEnd
    ) {
      return;
    }

    if (e.target.selectionStart === 1 || e.target.selectionStart === 2) {
      offsetAmount = 0;
    }

    this.updateControlValue(
      this._phoneNumberString.slice(0, e.target.selectionStart - offsetAmount) +
        e.key +
        this._phoneNumberString.slice(e.target.selectionEnd - offsetAmount, this._phoneNumberString.length),
      e.target.selectionStart +
        /* have to add an extra space if we're typing into the edge of a formatted area */
        (e.target.selectionStart === 2
          ? 3
          : e.target.selectionStart === 0 || e.target.selectionStart === 5 || e.target.selectionStart === 9
          ? 2
          : 1)
    );
  }

  private buildElementValue() {
    // As you type formatted
    return PhoneNumberUtil.asYouType(this._phoneNumberString, this.pniCountryCode);
  }

  private buildControlValue(): string {
    if (!PhoneNumberUtil.isValidPhoneNumber(this._phoneNumberString, this.pniCountryCode)) {
      return null;
    }

    // E.164 formatted
    return PhoneNumberUtil.formatPhoneNumberForRest(this._phoneNumberString, this.pniCountryCode);
  }
}
